/**
 * The contact list as a picker: team tabs (one per sheet of the uploaded workbook),
 * ONE search box that doubles as the "key in a number" field, and rows that show each
 * person's number. Shared by the standalone Contacts panel and the officer card's
 * "Send to WhatsApp" flow so the two are the same UI — the Contacts panel is the
 * source of truth for how it looks.
 *
 * Typing a number (digits, spaces, +, dashes) offers a "Send to <number>" row on top
 * and Enter sends to it; typing anything else filters the active team by name,
 * subteam or number.
 */
import { useState } from 'react';
import { WaIcon, subteamColor } from '@/components/WhatsAppShared';
import type { RecipientPill } from '@/components/WhatsAppShared';
import type { Contact, ContactList } from '@/types/api';

export interface PickedRecipient {
  /** As typed or stored; the caller normalises it. */
  phone: string;
  label: string;
  name?: string;
  pill?: RecipientPill;
}

/** Remembers the last team tab picked on this device; storage may be unavailable. */
const TEAM_TAB_KEY = 'contacts.activeTeam';
function loadTeamTab(): string | null {
  try { return localStorage.getItem(TEAM_TAB_KEY); } catch { return null; }
}
function saveTeamTab(team: string) {
  try { localStorage.setItem(TEAM_TAB_KEY, team); } catch { /* ignore */ }
}

/** Team D was the only team before per-team tabs, so it stays the default. */
function pickTeam(teams: string[], preferred: string | null): string | null {
  if (preferred && teams.includes(preferred)) return preferred;
  return teams.find((t) => t.toLowerCase() === 'team d') ?? teams[0] ?? null;
}

/** True when the box holds something that reads as a phone number rather than a name. */
function looksLikeNumber(query: string): boolean {
  return /^[\d\s+()-]+$/.test(query) && query.replace(/\D/g, '').length >= 3;
}

export function ContactPicker({
  data,
  emptyHint = 'No contacts available',
  onPick,
}: {
  /** null while loading. */
  data: ContactList | null;
  emptyHint?: string;
  onPick: (recipient: PickedRecipient) => void;
}) {
  const [query, setQuery] = useState('');
  const [chosenTeam, setChosenTeam] = useState<string | null>(loadTeamTab);

  const teams = data?.teams ?? [];
  const activeTeam = pickTeam(teams, chosenTeam);
  const contacts = data?.contacts ?? null;
  const q = query.trim().toLowerCase();
  const qDigits = q.replace(/\D/g, '');
  const numeric = looksLikeNumber(q);

  const filtered = (contacts ?? []).filter((c) => {
    if (activeTeam !== null && c.team !== activeTeam) return false;
    if (!q) return true;
    return c.name.toLowerCase().includes(q)
      || c.subteam.toLowerCase().includes(q)
      || (qDigits.length > 0 && c.phone.replace(/\D/g, '').includes(qDigits));
  });

  const pickContact = (c: Contact) => {
    if (!c.phone.trim()) return;
    onPick({
      phone: c.phone, label: c.name, name: c.name,
      pill: { label: c.subteam || '—', bg: subteamColor(c.subteam).bg, text: subteamColor(c.subteam).text },
    });
  };
  const pickTyped = () => {
    if (qDigits) onPick({ phone: qDigits, label: query.trim() });
  };

  const onEnter = () => {
    if (numeric) pickTyped();
    else if (filtered.length === 1) pickContact(filtered[0]);
  };

  return (
    <>
      {/* Team tabs — one per sheet in the uploaded workbook */}
      {teams.length > 0 && (
        <div role="tablist" style={{ display: 'flex', gap: 6, padding: '10px 18px 0', overflowX: 'auto', flexShrink: 0 }}>
          {teams.map((team) => {
            const active = team === activeTeam;
            return (
              <button
                key={team}
                role="tab"
                aria-selected={active}
                onClick={() => { setChosenTeam(team); saveTeamTab(team); }}
                style={{ padding: '5px 12px', borderRadius: 99, fontSize: 12, fontWeight: 600,
                  whiteSpace: 'nowrap', cursor: 'pointer', flexShrink: 0,
                  border: `1px solid ${active ? '#25D366' : '#d1d5db'}`,
                  background: active ? '#dcfce7' : 'white',
                  color: active ? '#0f7a3d' : '#475569' }}
              >
                {team}
              </button>
            );
          })}
        </div>
      )}

      {/* One box: search a name, or key in a number */}
      <div style={{ padding: '10px 18px', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
        <input
          autoFocus
          type="text"
          inputMode="search"
          placeholder="Search name, or key in a number…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') onEnter(); }}
          style={{ width: '100%', padding: '7px 10px', border: '1px solid #d1d5db',
            borderRadius: 7, fontSize: 13, boxSizing: 'border-box', outline: 'none' }}
        />
      </div>

      <div style={{ overflowY: 'auto', flex: 1 }}>
        {numeric && (
          <button
            onClick={pickTyped}
            style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '10px 18px',
              background: '#f0fdf4', border: 'none', borderBottom: '1px solid #bbf7d0',
              cursor: 'pointer', textAlign: 'left' }}
          >
            <WaIcon size={18} />
            <span style={{ fontSize: 13, fontWeight: 600, color: '#166534' }}>
              Send to {query.trim()}
            </span>
          </button>
        )}
        {contacts === null ? (
          <div style={{ padding: '24px', textAlign: 'center', fontSize: 13, color: '#94a3b8' }}>Loading…</div>
        ) : filtered.length === 0 ? (
          !numeric && (
            <div style={{ padding: '24px', textAlign: 'center', fontSize: 13, color: '#94a3b8' }}>
              {contacts.length === 0 ? emptyHint : 'No matches'}
            </div>
          )
        ) : (
          filtered.map((c, i) => {
            const hasPhone = !!c.phone.trim();
            return (
              <button
                key={i}
                onClick={() => pickContact(c)}
                disabled={!hasPhone}
                style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                  padding: '9px 18px', background: 'none', border: 'none',
                  borderBottom: '1px solid #f1f5f9', cursor: hasPhone ? 'pointer' : 'default',
                  opacity: hasPhone ? 1 : 0.5, textAlign: 'left' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#f8fafc'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'none'; }}
              >
                <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px',
                  borderRadius: '99px', flexShrink: 0, whiteSpace: 'nowrap',
                  background: subteamColor(c.subteam).bg, color: subteamColor(c.subteam).text }}>
                  {c.subteam || '—'}
                </span>
                <span style={{ fontSize: 13, fontWeight: 500, color: '#1e293b', flex: 1, minWidth: 0,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {c.name}
                </span>
                <span style={{ fontSize: 12, color: '#64748b', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                  {hasPhone ? c.phone : 'No number'}
                </span>
              </button>
            );
          })
        )}
      </div>
    </>
  );
}
